import { useCallback, useEffect, useState } from "react";
import { CalendarClock, BellRing, Info, Loader2, AlertTriangle } from "lucide-react";
import {
  fetchPaymentRequestStatus,
  useGeneratePaymentRequest,
  useGeneratePaymentReminder,
} from "../../../../api/document/useDocumentsApi";
import { useToast } from "../../../../context/ToastContext";
import { formatDMY } from "../../../../utils/dateFormat";
import { DocRow, EmptyState, SavingBanner, formatMoney } from "./shared";
import base from "../BookingDetail.module.css";
import s from "./documents.module.css";

/**
 * Payments panel: payment requests and overdue-payment reminders (Documents
 * spec §15).
 *
 * Finance issues them here from the booking's CURRENT outstanding balance; the
 * Send button hands them to the Documents centre, which checks the amount and
 * the due date again before anything goes out. A request may go out before its
 * due date; a reminder only exists once that date has passed unpaid.
 */
const PaymentsPanel = ({ bookingRef, docs, onIssue, onDownload, onView, onSend, downloading, newDocIds }) => {
  const { showToast } = useToast();
  const { execute: issueRequest, loading: issuingRequest } = useGeneratePaymentRequest();
  const { execute: issueReminder, loading: issuingReminder } = useGeneratePaymentReminder();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [dueDate, setDueDate] = useState("");

  const hasPendingSave = docs.some((d) => !d.pdfUrl && !d.pdfStuck);
  const busy = issuingRequest || issuingReminder;

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const next = await fetchPaymentRequestStatus(bookingRef);
      setStatus(next);
      if (next) {
        const keep = next.request?.dueDate && next.request.dueDate >= next.today ? next.request.dueDate : null;
        setDueDate(keep || next.suggestedDueDate || "");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Could not load the payment status of this booking", "error");
    } finally {
      setLoadingStatus(false);
    }
  }, [bookingRef, showToast]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleRequest = async () => {
    if (!dueDate) {
      showToast("Choose a due date first", "error");
      return;
    }
    await onIssue({ api: () => issueRequest(bookingRef, { dueDate }), noun: "Payment request" });
    loadStatus();
  };

  const handleReminder = async () => {
    await onIssue({ api: () => issueReminder(bookingRef), noun: "Payment reminder" });
    loadStatus();
  };

  const request = status?.request || null;
  const reminder = status?.reminder || null;
  const outdated = request && !request.current && status?.canRequest;

  return (
    <div>
      <div className={s.panelHead}>
        <div>
          <div className={s.panelTitle}><CalendarClock size={16} /> Payment requests</div>
          <div className={s.panelSubtitle}>
            {docs.length} document{docs.length !== 1 ? "s" : ""} · requests for the outstanding balance and overdue reminders
          </div>
        </div>
      </div>

      <div className={s.infoBanner}>
        <Info size={15} />
        <span>
          The amount is always the booking’s <strong>current outstanding balance</strong> from Finance.
          Issue the document here, then use <strong>Send</strong>: the Documents centre checks the amount
          and the due date again before anything goes out.
        </span>
      </div>

      <div className={s.summaryStrip}>
        <div className={s.summaryCard}>
          <div className={s.summaryLabel}>Outstanding balance</div>
          <div className={s.summaryValue}>
            {loadingStatus && !status ? "…" : formatMoney(status?.outstanding ?? 0)}
          </div>
        </div>
        <div className={s.summaryCard}>
          <div className={s.summaryLabel}>Payment request</div>
          <div className={s.summaryValue}>{request ? request.number : "None yet"}</div>
          {request && (
            <div className={s.payMeta}>
              {formatMoney(request.amount)} · due {formatDMY(request.dueDate)}
            </div>
          )}
        </div>
        <div className={s.summaryCard}>
          <div className={s.summaryLabel}>Payment reminder</div>
          <div className={s.summaryValue}>{reminder ? reminder.number : "None yet"}</div>
          {reminder && (
            <div className={s.payMeta}>
              {formatMoney(reminder.amount)} · was due {formatDMY(reminder.dueDate)}
            </div>
          )}
        </div>
      </div>

      {outdated && (
        <div className={s.staleBanner} role="status">
          <AlertTriangle size={15} />
          <span>
            {request.number} asks for {formatMoney(request.amount)}, but the balance is now{" "}
            {formatMoney(status.outstanding)}. Re-issue it before sending.
          </span>
        </div>
      )}

      <div className={s.payActions}>
        <div className={s.payCard}>
          <div className={s.payCardTitle}><CalendarClock size={14} /> {request ? "Re-issue payment request" : "Payment request"}</div>
          <div className={s.payCardHint}>
            {status?.canRequest
              ? "Asks for the outstanding balance by the due date. It may be sent before that date."
              : status?.requestBlock || "Loading…"}
          </div>
          <div className={s.payCardRow}>
            <label className={s.payField}>
              <span>Due date</span>
              <input
                type="date"
                className={s.payInput}
                value={dueDate}
                min={status?.today || undefined}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={!status?.canRequest || busy}
              />
            </label>
            <button
              type="button"
              className={base.btnPrimary}
              onClick={handleRequest}
              disabled={!status?.canRequest || busy || hasPendingSave || loadingStatus}
            >
              {issuingRequest ? <Loader2 size={13} style={{ animation: "docSpin 1s linear infinite" }} /> : <CalendarClock size={13} />}
              {issuingRequest ? "Issuing…" : request ? "Re-issue request" : "Issue request"}
            </button>
          </div>
        </div>

        <div className={s.payCard}>
          <div className={s.payCardTitle}><BellRing size={14} /> Payment reminder</div>
          <div className={s.payCardHint}>
            {status?.canRemind
              ? `${request?.number} was due on ${formatDMY(request?.dueDate)} (${status.daysOverdue} day${status.daysOverdue === 1 ? "" : "s"} ago) and is still unpaid.`
              : status?.reminderBlock || "Loading…"}
          </div>
          <div className={s.payCardRow}>
            <button
              type="button"
              className={base.btnPrimary}
              onClick={handleReminder}
              disabled={!status?.canRemind || busy || hasPendingSave || loadingStatus}
            >
              {issuingReminder ? <Loader2 size={13} style={{ animation: "docSpin 1s linear infinite" }} /> : <BellRing size={13} />}
              {issuingReminder ? "Issuing…" : reminder ? "Re-issue reminder" : "Issue reminder"}
            </button>
          </div>
        </div>
      </div>

      {docs.length === 0 ? (
        <EmptyState
          Icon={CalendarClock}
          title="No payment requests yet"
          hint="Issue a payment request when the customer still owes part of the booking. A reminder becomes possible once its due date has passed."
        />
      ) : (
        <div className={s.list}>
          {docs.map((doc, i) => (
            <DocRow
              key={doc.id}
              doc={doc}
              index={i}
              isNew={newDocIds.has(doc.id)}
              onDownload={onDownload}
              onView={onView}
              onSend={onSend}
              downloading={downloading}
              regenerating={busy}
            />
          ))}
          {hasPendingSave && <SavingBanner />}
        </div>
      )}
    </div>
  );
};

export default PaymentsPanel;
